import { useQuery } from '@tanstack/react-query';
import { BookOpen, Link as LinkIcon } from 'lucide-react-native';
import { useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Image } from '@/components/ui/image';
import { Pressable } from '@/components/ui/pressable';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { getLinkPreviewQueryOptions, linkHost } from '@/services/conversations/linkPreview';
import {
  getPublicationLookupQueryOptions,
  opensAsReference,
} from '@/services/conversations/publicationLookup';
import { toOpenableUrl } from '@/util/messageLinks';

const CARD_CLASS =
  'mt-2 w-72 max-w-full overflow-hidden rounded-xl border border-l-4 border-outline-200 border-l-primary-500 bg-background-0 px-3 py-2';

/**
 * A link's unfurled preview, under the message that carries it, the way Slack
 * and iMessage show one: the site, then the page's title, then for an article
 * where and when it ran, with the page's own preview image as a thumbnail
 * beside them when it names one. The whole card opens the link.
 *
 * The thumbnail sits to the side, as Slack draws one, rather than iMessage's
 * full-width banner: a bubble is narrow, and many sites' preview image is a
 * logo that a banner would blow up. It loads straight from the site, so one
 * that fails to load is dropped and the text stands alone.
 *
 * It keeps its own light surface on either fill, so a preview reads the same in
 * the author's green bubble as in the other party's. The brand edge marks it as
 * an attachment rather than more of the message.
 *
 * While the preview loads the card holds its shape with the site already
 * named, so the thread does not jump when the title lands. A link that cannot
 * be read unfurls to nothing and stays a plain link in the text above.
 */
export function LinkPreviewCard({
  url,
  onOpen,
}: {
  url: string;
  /** Opens the link: the reference sheet for a catalog work, the in-app browser otherwise. */
  onOpen: (url: string) => void;
}) {
  const { data: preview, isPending } = useQuery(getLinkPreviewQueryOptions(url));

  /** Looked up with the preview, so a press opens the reference sheet without waiting. */
  const { data: catalogWork } = useQuery(getPublicationLookupQueryOptions(toOpenableUrl(url)));

  const [imageFailed, setImageFailed] = useState(false);

  if (isPending) {
    return (
      <VStack className={CARD_CLASS} aria-hidden>
        <Text className="text-xs text-typography-600" numberOfLines={1}>
          {linkHost(url)}
        </Text>
        <Skeleton className="mt-2 h-4 w-11/12 rounded" />
        <Skeleton className="mt-1 h-4 w-2/3 rounded" />
      </VStack>
    );
  }

  if (!preview) {
    return null;
  }

  const isArticle = Boolean(preview.source || preview.authors) || opensAsReference(catalogWork);

  return (
    <Pressable
      onPress={() => onOpen(url)}
      accessibilityRole="link"
      accessibilityLabel={`${preview.title}, ${preview.host}`}
      accessibilityHint="Opens the link"
      className={twJoin(
        CARD_CLASS,
        'web:cursor-pointer data-[hover=true]:bg-background-50 data-[active=true]:bg-background-50'
      )}>
      <HStack className="items-start gap-3">
        <VStack className="min-w-0 flex-1">
          <HStack className="items-center gap-1.5">
            <Icon
              as={isArticle ? BookOpen : LinkIcon}
              className="h-3.5 w-3.5 text-typography-600"
            />
            <Text className="min-w-0 flex-1 text-xs text-typography-600" numberOfLines={1}>
              {preview.host}
            </Text>
          </HStack>

          <Text className="mt-1 font-semibold text-sm text-secondary-800" numberOfLines={2}>
            {preview.title}
          </Text>

          {preview.source ? (
            <Text className="mt-0.5 text-xs text-typography-700" numberOfLines={1}>
              {preview.source}
            </Text>
          ) : null}

          {preview.authors ? (
            <Text className="mt-0.5 text-xs text-typography-600" numberOfLines={1}>
              {preview.authors}
            </Text>
          ) : null}
        </VStack>

        {preview.imageUrl && !imageFailed ? (
          <Image
            source={{ uri: preview.imageUrl }}
            alt=""
            size="sm"
            resizeMode="cover"
            onError={() => setImageFailed(true)}
            className="rounded-lg bg-background-100"
            accessibilityIgnoresInvertColors
            aria-hidden
          />
        ) : null}
      </HStack>
    </Pressable>
  );
}
